import { useUser } from '@/hooks/useUser'
import { useNavigate, Link } from 'react-router-dom'
import { useEffect, useState, useRef } from 'react'
import { Property, propertyApi } from '@/lib/api'

// ===== ANIMATED COUNTER COMPONENT =====
// Displays statistics with a counting animation for visual impact
function AnimatedCounter({ end, duration = 2000, suffix = '' }: { end: number; duration?: number; suffix?: string }) {
  const [count, setCount] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    // Use IntersectionObserver to trigger animation when visible
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!isVisible) return

    let startTime: number
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime
      const progress = Math.min((currentTime - startTime) / duration, 1)
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4)
      setCount(Math.floor(easeOutQuart * end))
      if (progress < 1) requestAnimationFrame(animate)
    }
    requestAnimationFrame(animate)
  }, [isVisible, end, duration])

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>
}

// ===== TESTIMONIAL DATA =====
// Real-looking testimonials for social proof
const testimonials = [
  {
    name: 'Adaeze Okonkwo',
    role: 'Tenant in Lagos',
    content: 'I saved over ₦500,000 in agent fees! Found my perfect apartment in Lekki within a week. The direct communication with the landlord made everything so smooth.',
    avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
    rating: 5
  },
  {
    name: 'Chinedu Eze',
    role: 'Property Owner',
    content: 'As a landlord, I love the verified tenant system. I\'ve found reliable tenants faster than through any agent. The platform handles everything professionally.',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
    rating: 5
  },
  {
    name: 'Fatima Ibrahim',
    role: 'Tenant in Abuja',
    content: 'No more dealing with unreliable agents! The property verification gave me confidence. Moved into my new home in Gwarinpa hassle-free.',
    avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
    rating: 5
  }
]

// ===== HOW IT WORKS STEPS =====
const howItWorksSteps = [
  {
    step: 1,
    title: 'Browse Properties',
    description: 'Explore verified listings with real photos, videos, and detailed information. Filter by location, price, and amenities.',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    )
  },
  {
    step: 2,
    title: 'Connect Directly',
    description: 'Message landlords instantly. Schedule viewings, ask questions, and negotiate terms—all without middlemen.',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    )
  },
  {
    step: 3,
    title: 'Move In & Save',
    description: 'Complete secure payments, sign your agreement, and move into your new home. Keep thousands in your pocket!',
    icon: (
      <img src="/logo.png" alt="Logo" className="w-12 h-12 object-contain" />
    )
  }
]

export default function HomePage() {
  const { user, loading } = useUser()
  const navigate = useNavigate()
  const [showLanding, setShowLanding] = useState(false)
  const [verifiedProperties, setVerifiedProperties] = useState<Property[]>([])
  const [loadingProperties, setLoadingProperties] = useState(true)
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set())
  const [selectedRentalType, setSelectedRentalType] = useState<'all' | 'short-term' | 'long-term'>('all')

  useEffect(() => {
    if (!loading) {
      if (user) {
        // Redirect authenticated users to their appropriate dashboard
        if (user.role === 'landlord') {
          navigate('/dashboard/landlord')
        } else if (user.role === 'client') {
          navigate('/dashboard/client')
        } else if (user.role === 'admin') {
          navigate('/admin/dashboard')
        }
      } else {
        // Show landing page for unauthenticated users
        setShowLanding(true)
        loadVerifiedProperties()
      }
    }
  }, [user, loading, navigate])

  // Load verified properties from the API
  const loadVerifiedProperties = async () => {
    setLoadingProperties(true)
    try {
      const response = await propertyApi.getProperties({
        verified: true,
        limit: 6
      })
      
      if (!response.success) {
        console.warn('Backend API not available, using mock data:', response.error)
        setVerifiedProperties(getMockProperties())
        return
      }
      
      // Process and normalize property data
      const processedProperties = (response.data?.properties || []).map((property: any) => {
        let imageUrls: string[] = []
        
        if (property.images && property.images.length > 0) {
          const rawUrls = property.images.map((img: any) => {
            if (typeof img === 'string') return img
            else if (img && typeof img === 'object' && img.url) return img.url
            return null
          }).filter((url: string | null) => url && url.trim() !== '')
          
          // Normalize localhost URLs
          imageUrls = rawUrls.map((url: string) => {
            if (!url) return url
            if (url.includes('localhost:5001') || url.includes('127.0.0.1:5001')) {
              const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
              const apiBaseUrl = apiUrl.replace(/\/api\/?$/, '')
              try {
                const urlObj = new URL(url)
                return `${apiBaseUrl}${urlObj.pathname}${urlObj.search}${urlObj.hash}`
              } catch {
                return url.replace(/https?:\/\/[^/]+/, apiBaseUrl)
              }
            } else if (url.startsWith('/uploads')) {
              const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
              const apiBaseUrl = apiUrl.replace(/\/api\/?$/, '')
              return `${apiBaseUrl}${url}`
            }
            return url
          })
        }
        
        // Build location string from address
        let location = property.location
        if (!location && property.address && typeof property.address === 'object') {
          const addr = property.address
          const parts = []
          if (addr.street) parts.push(addr.street)
          if (addr.city) parts.push(addr.city)
          if (addr.state) parts.push(addr.state)
          if (parts.length > 0) location = parts.join(', ')
        }
        
        return {
          ...property,
          images: imageUrls,
          location: location || property.location || 'Location not specified',
          id: property._id || property.id,
          _id: property._id || property.id
        }
      })
      setVerifiedProperties(processedProperties)
    } catch (error) {
      console.warn('Backend API not available, using mock data:', error)
      setVerifiedProperties(getMockProperties())
    } finally {
      setLoadingProperties(false)
    }
  }

  // Mock properties for demo/fallback
  const getMockProperties = (): Property[] => [
    {
      _id: 'mock-1',
      title: 'Luxury 3-Bedroom Apartment',
      description: 'Beautiful spacious apartment with modern finishes and stunning city views. Perfect for families.',
      price: 2500000,
      location: 'Victoria Island, Lagos',
      images: ['https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&h=600&fit=crop'],
      bedrooms: 3,
      bathrooms: 3,
      area: 180,
      propertyType: 'apartment',
      status: 'available',
      isVerified: true,
      landlordId: 'mock-landlord-1',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      _id: 'mock-2',
      title: 'Cozy 2-Bedroom Bungalow',
      description: 'Perfect family home with a large compound and modern amenities. Quiet neighborhood.',
      price: 1800000,
      location: 'Ikeja GRA, Lagos',
      images: ['https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=800&h=600&fit=crop'],
      bedrooms: 2,
      bathrooms: 2,
      area: 120,
      propertyType: 'house',
      status: 'available',
      isVerified: true,
      landlordId: 'mock-landlord-2',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      _id: 'mock-3',
      title: 'Executive Penthouse Suite',
      description: 'Exclusive penthouse with panoramic ocean views and premium finishes. Rooftop access.',
      price: 5500000,
      location: 'Ikoyi, Lagos',
      images: ['https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&h=600&fit=crop'],
      bedrooms: 4,
      bathrooms: 4,
      area: 300,
      propertyType: 'penthouse',
      status: 'available',
      isVerified: true,
      landlordId: 'mock-landlord-3',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  ]

  // Format price in Naira
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      maximumFractionDigits: 0,
    }).format(price)
  }

  // Loading state with elegant spinner
  if (loading) {
    return (
      <div className="min-h-screen mesh-gradient flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-6 text-stone-600 font-medium">Loading LandLordNoAgent...</p>
        </div>
      </div>
    )
  }

  if (!showLanding) return null

  // ===== MAIN LANDING PAGE =====
  return (
    <div className="min-h-screen bg-stone-50">
      {/* ===== NAVIGATION ===== */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 md:h-24 py-2">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img src="/logo.png" alt="LandlordNoAgent Logo" className="w-28 h-28 md:w-32 md:h-32 object-contain" />
            </Link>

            {/* Nav Links */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#properties" className="text-stone-600 hover:text-emerald-600 transition-colors font-medium">
                Properties
              </a>
              <a href="#how-it-works" className="text-stone-600 hover:text-emerald-600 transition-colors font-medium">
                How It Works
              </a>
              <a href="#testimonials" className="text-stone-600 hover:text-emerald-600 transition-colors font-medium">
                Testimonials
              </a>
              <Link to="/careers" className="text-stone-600 hover:text-emerald-600 transition-colors font-medium">
                Careers
              </Link>
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center space-x-3">
              <Link 
                to="/auth?mode=signin" 
                className="text-stone-700 hover:text-emerald-600 px-4 py-2 font-medium transition-colors hidden sm:block"
              >
                Sign In
              </Link>
              <Link 
                to="/auth?mode=signup" 
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/25"
              >
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* ===== HERO SECTION ===== */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 overflow-hidden">
        {/* Background gradient mesh */}
        <div className="absolute inset-0 mesh-gradient"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-300/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-amber-300/20 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div className="text-center lg:text-left">
              {/* Trust badge */}
              <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6 animate-fade-in-up">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span>Trusted by 5,000+ Nigerians</span>
              </div>

              {/* Main headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display text-stone-900 mb-6 animate-fade-in-up animation-delay-100">
                Rent Directly.
                <br />
                <span className="text-emerald-600">Save More.</span>
              </h1>

              {/* Subheadline */}
              <p className="text-lg sm:text-xl text-stone-600 mb-8 max-w-xl mx-auto lg:mx-0 animate-fade-in-up animation-delay-200">
                Skip the middlemen and connect directly with verified landlords and tenants. 
                <span className="font-semibold text-stone-800"> Save up to ₦500,000</span> in agent fees.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up animation-delay-300">
                <Link
                  to="/auth"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/30 transform hover:-translate-y-0.5 flex items-center justify-center space-x-2"
                >
                  <span>Start Searching</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                <Link
                  to="/auth"
                  className="bg-white hover:bg-stone-50 text-stone-800 px-8 py-4 rounded-xl text-lg font-semibold border-2 border-stone-200 transition-all duration-300 hover:border-emerald-300 hover:shadow-lg"
                >
                  List Your Property
                </Link>
              </div>

              {/* Social proof mini stats */}
              <div className="mt-10 flex items-center justify-center lg:justify-start space-x-8 animate-fade-in-up animation-delay-400">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <img
                      key={i}
                      src={`https://randomuser.me/api/portraits/${i % 2 === 0 ? 'women' : 'men'}/${20 + i}.jpg`}
                      alt=""
                      className="w-10 h-10 rounded-full border-2 border-white"
                    />
                  ))}
                </div>
                <div className="text-left">
                  <p className="text-stone-900 font-semibold">Join 5,000+ users</p>
                  <div className="flex items-center space-x-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <svg key={i} className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-stone-500 text-sm ml-1">4.9/5 rating</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Image/Visual */}
            <div className="relative animate-fade-in-up animation-delay-200">
              <div className="relative">
                {/* Main property image */}
                <div className="rounded-3xl overflow-hidden shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-500">
                  <img
                    src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop"
                    alt="Beautiful modern home"
                    className="w-full h-[400px] object-cover"
                  />
                </div>

                {/* Floating card - Savings */}
                <div className="absolute -left-4 top-1/4 bg-white rounded-2xl shadow-xl p-4 animate-float">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-stone-500">Average Savings</p>
                      <p className="text-lg font-bold text-emerald-600">₦500,000+</p>
                    </div>
                  </div>
                </div>

                {/* Floating card - Verified */}
                <div className="absolute -right-4 bottom-1/4 bg-white rounded-2xl shadow-xl p-4 animate-float animation-delay-300">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-stone-500">All Properties</p>
                      <p className="text-lg font-bold text-stone-800">100% Verified</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== STATS SECTION ===== */}
      <section className="py-16 bg-white border-y border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <p className="stat-number"><AnimatedCounter end={2500} suffix="+" /></p>
              <p className="text-stone-600 mt-2 font-medium">Verified Properties</p>
            </div>
            <div className="text-center">
              <p className="stat-number"><AnimatedCounter end={5000} suffix="+" /></p>
              <p className="text-stone-600 mt-2 font-medium">Happy Users</p>
            </div>
            <div className="text-center">
              <p className="stat-number">₦<AnimatedCounter end={2} suffix="B+" /></p>
              <p className="text-stone-600 mt-2 font-medium">Saved in Fees</p>
            </div>
            <div className="text-center">
              <p className="stat-number"><AnimatedCounter end={98} suffix="%" /></p>
              <p className="text-stone-600 mt-2 font-medium">Satisfaction Rate</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
              Simple Process
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display text-stone-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Finding your perfect home has never been easier. Three simple steps to skip the agent and save big.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {howItWorksSteps.map((item, index) => (
              <div
                key={item.step}
                className="relative bg-white rounded-2xl p-8 shadow-sm border border-stone-100 hover:shadow-xl hover:border-emerald-200 transition-all duration-300 group"
              >
                {/* Step number */}
                <div className="absolute -top-4 left-8 bg-emerald-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
                  {item.step}
                </div>
                
                {/* Icon */}
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center text-emerald-600 mb-6 group-hover:bg-emerald-600 group-hover:text-white transition-colors duration-300">
                  {item.icon}
                </div>
                
                <h3 className="text-xl font-display text-stone-900 mb-3">{item.title}</h3>
                <p className="text-stone-600 leading-relaxed">{item.description}</p>

                {/* Connector line (not on last item) */}
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 border-t-2 border-dashed border-emerald-200"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURED PROPERTIES ===== */}
      <section id="properties" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <span className="inline-block bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                Featured Listings
              </span>
              <h2 className="text-3xl sm:text-4xl font-display text-stone-900 mb-2">
                Explore Verified Properties
              </h2>
              <p className="text-stone-600 max-w-xl">
                Hand-picked properties from verified landlords. Every listing is inspected for quality.
              </p>
            </div>

            {/* Rental type filter */}
            <div className="flex gap-2 mt-6 md:mt-0">
              {['all', 'long-term', 'short-term'].map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedRentalType(type as typeof selectedRentalType)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                    selectedRentalType === type
                      ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/25'
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  {type === 'all' ? 'All' : type === 'long-term' ? 'Long-term' : 'Shortlets'}
                </button>
              ))}
            </div>
          </div>

          {/* Property Grid */}
          {loadingProperties ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                  <div className="h-64 bg-stone-200"></div>
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-stone-200 rounded w-3/4"></div>
                    <div className="h-3 bg-stone-200 rounded w-1/2"></div>
                    <div className="h-6 bg-stone-200 rounded w-1/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {verifiedProperties
                .filter(property => {
                  if (selectedRentalType === 'all') return true
                  return (property as any).rentalType === selectedRentalType
                })
                .map((property, index) => (
                  <div
                    key={property._id}
                    className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-stone-100 hover:shadow-xl hover:border-emerald-200 transition-all duration-500 animate-fade-in-up"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {/* Property Image */}
                    <div className="relative h-64 bg-stone-100 overflow-hidden">
                      {/* Badges */}
                      <div className="absolute top-4 left-4 z-10 flex gap-2">
                        <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Verified
                        </span>
                        {(property as any).rentalType && (
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            (property as any).rentalType === 'short-term'
                              ? 'bg-amber-500 text-white'
                              : 'bg-stone-800 text-white'
                          }`}>
                            {(property as any).rentalType === 'short-term' ? 'Shortlet' : 'Long-term'}
                          </span>
                        )}
                      </div>

                      {(() => {
                        const firstImage = property.images && property.images.length > 0 
                          ? (typeof property.images[0] === 'string' 
                              ? property.images[0] 
                              : (property.images[0] as any)?.url || property.images[0])
                          : null
                        
                        return firstImage && !imageErrors.has(firstImage) ? (
                          <img
                            src={firstImage}
                            alt={property.title || 'Property image'}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            onError={() => setImageErrors(prev => new Set([...prev, firstImage]))}
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-stone-100">
                            <svg className="w-16 h-16 text-stone-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h4a1 1 0 011 1v5m-6 0V9a1 1 0 011-1h4a1 1 0 011 1v2" />
                            </svg>
                          </div>
                        )
                      })()}

                      {/* Image count badge */}
                      {property.images && property.images.length > 1 && (
                        <div className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {property.images.length}
                        </div>
                      )}
                    </div>

                    {/* Property Details */}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-semibold text-stone-900 line-clamp-1 group-hover:text-emerald-600 transition-colors">
                          {property.title}
                        </h3>
                      </div>

                      <div className="flex items-center text-stone-500 text-sm mb-4">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="line-clamp-1">{property.location}</span>
                      </div>

                      {/* Property features */}
                      <div className="flex items-center gap-4 mb-4 text-sm text-stone-600">
                        {property.bedrooms && (
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                            </svg>
                            <span>{property.bedrooms} bed</span>
                          </div>
                        )}
                        {property.bathrooms && (
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>{property.bathrooms} bath</span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                        <div>
                          <p className="text-2xl font-bold text-emerald-600">
                            {formatPrice(property.price)}
                          </p>
                          <p className="text-xs text-stone-500">
                            {(property as any).rentalType === 'short-term' ? 'per night' : 'per year'}
                          </p>
                        </div>
                        <Link
                          to={`/property/${property._id}`}
                          className="bg-stone-900 hover:bg-emerald-600 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors duration-300"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* View all CTA */}
          <div className="text-center mt-12">
            <Link
              to="/auth"
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/25"
            >
              Browse All Properties
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section id="testimonials" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-amber-100 text-amber-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
              Success Stories
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display text-stone-900 mb-4">
              What Our Users Say
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Join thousands of satisfied landlords and tenants who've transformed their rental experience.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="testimonial-card hover:shadow-xl transition-shadow duration-300"
              >
                {/* Stars */}
                <div className="flex gap-1 mb-4 pt-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>

                <p className="text-stone-600 mb-6 leading-relaxed">{testimonial.content}</p>

                <div className="flex items-center gap-3">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-stone-900">{testimonial.name}</p>
                    <p className="text-sm text-stone-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-800 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 pattern-grid"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display text-white mb-6">
            Ready to Save Money on Your Next Rental?
          </h2>
          <p className="text-xl text-emerald-100 mb-10 max-w-2xl mx-auto">
            Join thousands of smart Nigerians who skip the agent fees. 
            Your perfect home is just a few clicks away.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/auth"
              className="bg-white hover:bg-stone-100 text-emerald-700 px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Get Started Free
            </Link>
            <Link
              to="/auth"
              className="bg-emerald-700/50 hover:bg-emerald-700 text-white border-2 border-emerald-400/50 px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300"
            >
              List Your Property
            </Link>
          </div>

          {/* Trust indicators */}
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-emerald-200">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>No hidden fees</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Verified properties</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Secure payments</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-stone-900 text-stone-300 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            {/* Brand column */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center space-x-2 mb-4">
                <img src="/logo.png" alt="LandlordNoAgent Logo" className="w-14 h-14 object-contain" />
                <span className="text-xl font-bold text-white">
                  Landlord<span className="text-emerald-400">NoAgent</span>
                </span>
              </div>
              <p className="text-sm text-stone-400 mb-4">
                Connecting landlords and tenants directly for better rental experiences across Nigeria.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-stone-400 hover:text-emerald-400 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="#" className="text-stone-400 hover:text-emerald-400 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>
                <a href="#" className="text-stone-400 hover:text-emerald-400 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.757-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#properties" className="hover:text-emerald-400 transition-colors">Browse Properties</a></li>
                <li><a href="#how-it-works" className="hover:text-emerald-400 transition-colors">How It Works</a></li>
                <li><a href="#testimonials" className="hover:text-emerald-400 transition-colors">Testimonials</a></li>
                <li><Link to="/auth" className="hover:text-emerald-400 transition-colors">List Property</Link></li>
                <li><Link to="/careers" className="hover:text-emerald-400 transition-colors">Careers</Link></li>
              </ul>
            </div>

            {/* For Renters */}
            <div>
              <h4 className="text-white font-semibold mb-4">For Renters</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/auth" className="hover:text-emerald-400 transition-colors">Find a Home</Link></li>
                <li><Link to="/auth" className="hover:text-emerald-400 transition-colors">Shortlet Stays</Link></li>
                <li><Link to="/auth" className="hover:text-emerald-400 transition-colors">Long-term Rentals</Link></li>
                <li><Link to="/auth" className="hover:text-emerald-400 transition-colors">Saved Properties</Link></li>
              </ul>
            </div>

            {/* For Landlords */}
            <div>
              <h4 className="text-white font-semibold mb-4">For Landlords</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/auth" className="hover:text-emerald-400 transition-colors">List Your Property</Link></li>
                <li><Link to="/auth" className="hover:text-emerald-400 transition-colors">Landlord Dashboard</Link></li>
                <li><Link to="/auth" className="hover:text-emerald-400 transition-colors">Pricing</Link></li>
                <li><Link to="/auth" className="hover:text-emerald-400 transition-colors">Verification</Link></li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 border-t border-stone-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-stone-500">
              © {new Date().getFullYear()} LandLordNoAgent. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/legal/privacy" className="text-stone-400 hover:text-emerald-400 transition-colors">Privacy Policy</Link>
              <Link to="/legal/terms" className="text-stone-400 hover:text-emerald-400 transition-colors">Terms of Service</Link>
              <Link to="/help" className="text-stone-400 hover:text-emerald-400 transition-colors">Contact</Link>
              <Link to="/careers" className="text-stone-400 hover:text-emerald-400 transition-colors">Careers</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
